// Synthesized sound: engine drone, guns, hits, explosions, wind.
// Pure WebAudio oscillators/noise — no audio assets.

export class GameAudio {
  constructor() {
    this.ctx = null;
    this.muted = localStorage.getItem("va-muted") === "1";
    this.flybys = new Map(); // plane id -> positional engine voice
  }

  ensure() {
    if (this.ctx) {
      // iOS suspends the context whenever the tab/app backgrounds; calling
      // ensure() again from a fresh user gesture is a cheap way to revive it.
      this.resume();
      return;
    }
    // DELIBERATE: no audioSession "playback" claim here. iOS routes Web
    // Audio as "ambient", which the ring/silent switch mutes completely —
    // so a phone on silent plays a silent war. We tried claiming "playback"
    // (music-app routing, switch ignored) and the verdict came back: the
    // switch means silent, respect it. The cost is a support question —
    // "no audio on my iPhone" is almost always the switch — answered by the
    // hint on the touch join screen, not by overriding the player's phone.
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    this.ctx = ctx;
    // iOS Safari can hand back a context that starts "suspended" even when it
    // was created inside a tap handler; resume() inside that same gesture
    // unlocks it.
    ctx.resume?.().catch(() => {});
    // And iOS re-suspends it on interruptions — a phone call, Control
    // Center, a backgrounded PWA — without reliably firing visibilitychange
    // on the way back. Any next touch revives it: passive, and resume() is
    // a no-op while the context is already running.
    addEventListener("touchend", () => this.resume(), { passive: true });
    addEventListener("pointerup", () => this.resume(), { passive: true });

    this.master = ctx.createGain();
    this.master.gain.value = this.muted ? 0 : 0.5;
    this.master.connect(ctx.destination);

    // Piston engine: two detuned saws through a lowpass — the WW2 drone.
    this.engineGain = ctx.createGain();
    this.engineGain.gain.value = 0;
    const engineFilter = ctx.createBiquadFilter();
    engineFilter.type = "lowpass";
    engineFilter.frequency.value = 360;
    this.osc1 = ctx.createOscillator();
    this.osc2 = ctx.createOscillator();
    this.osc1.type = "sawtooth";
    this.osc2.type = "sawtooth";
    this.osc1.frequency.value = 60;
    this.osc2.frequency.value = 61.7;
    this.osc1.connect(this.engineGain);
    this.osc2.connect(this.engineGain);
    this.engineGain.connect(engineFilter);
    engineFilter.connect(this.master);
    this.osc1.start();
    this.osc2.start();

    // Turbojet: no pistons, no drone. The voice is a broadband thrust roar
    // (looped noise through a lowpass that opens as the engine spools) with a
    // thin turbine whine on top (a sawtooth whistling through a tight
    // bandpass that climbs with the spool). setFlight picks this chain or the
    // piston chain from the airframe's CLASS_JET flag; both idle at 0 gain.
    const jetBuf = this.noiseBuffer(2.7);
    this.jetSrc = ctx.createBufferSource();
    this.jetSrc.buffer = jetBuf;
    this.jetSrc.loop = true;
    this.jetFilter = ctx.createBiquadFilter();
    this.jetFilter.type = "lowpass";
    this.jetFilter.frequency.value = 400;
    this.jetGain = ctx.createGain();
    this.jetGain.gain.value = 0;
    this.jetSrc.connect(this.jetFilter);
    this.jetFilter.connect(this.jetGain);
    this.jetGain.connect(this.master);
    this.jetSrc.start();

    this.whineOsc = ctx.createOscillator();
    this.whineOsc.type = "sawtooth";
    this.whineOsc.frequency.value = 1100;
    this.whineFilter = ctx.createBiquadFilter();
    this.whineFilter.type = "bandpass";
    this.whineFilter.frequency.value = 1100;
    this.whineFilter.Q.value = 6;
    this.whineGain = ctx.createGain();
    this.whineGain.gain.value = 0;
    this.whineOsc.connect(this.whineFilter);
    this.whineFilter.connect(this.whineGain);
    this.whineGain.connect(this.master);
    this.whineOsc.start();

    // Wind: looped filtered noise, louder with speed.
    const windBuf = this.noiseBuffer(2);
    this.windSrc = ctx.createBufferSource();
    this.windSrc.buffer = windBuf;
    this.windSrc.loop = true;
    this.windFilter = ctx.createBiquadFilter();
    this.windFilter.type = "bandpass";
    this.windFilter.frequency.value = 900;
    this.windGain = ctx.createGain();
    this.windGain.gain.value = 0;
    this.windSrc.connect(this.windFilter);
    this.windFilter.connect(this.windGain);
    this.windGain.connect(this.master);
    this.windSrc.start();

    // Ship engine + bow wash (N4): a low lowpass-filtered noise rumble whose
    // pitch/level track the throttle, plus a hissier bow-wash bandpass that rises
    // with speed — so conning a hull has presence instead of dead silence.
    const shipBuf = this.noiseBuffer(3);
    this.shipEngSrc = ctx.createBufferSource();
    this.shipEngSrc.buffer = shipBuf;
    this.shipEngSrc.loop = true;
    this.shipEngFilter = ctx.createBiquadFilter();
    this.shipEngFilter.type = "lowpass";
    this.shipEngFilter.frequency.value = 90;
    this.shipEngGain = ctx.createGain();
    this.shipEngGain.gain.value = 0;
    this.shipEngSrc.connect(this.shipEngFilter);
    this.shipEngFilter.connect(this.shipEngGain);
    this.shipEngGain.connect(this.master);
    this.shipEngSrc.start();

    this.washSrc = ctx.createBufferSource();
    this.washSrc.buffer = shipBuf;
    this.washSrc.loop = true;
    this.washFilter = ctx.createBiquadFilter();
    this.washFilter.type = "bandpass";
    this.washFilter.frequency.value = 700;
    this.washFilter.Q.value = 0.5;
    this.washGain = ctx.createGain();
    this.washGain.gain.value = 0;
    this.washSrc.connect(this.washFilter);
    this.washFilter.connect(this.washGain);
    this.washGain.connect(this.master);
    this.washSrc.start();
  }

  /**
   * Drive the warship engine + bow-wash loops while conning a hull (N4).
   * throttle 0..1, speed m/s, boost lifts the engine note. Pass active=false
   * (not driving) to fade both out.
   */
  setShipEngine(throttle, speed, active, boost = false) {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const eng = active ? 0.12 + Math.abs(throttle) * 0.14 + (boost ? 0.05 : 0) : 0;
    this.shipEngGain.gain.setTargetAtTime(eng, t, 0.15);
    this.shipEngFilter.frequency.setTargetAtTime(70 + Math.abs(throttle) * 70 + (boost ? 40 : 0), t, 0.2);
    this.washGain.gain.setTargetAtTime(active ? Math.min(speed / 30, 1) * 0.09 : 0, t, 0.25);
  }

  noiseBuffer(seconds) {
    const len = this.ctx.sampleRate * seconds;
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    return buf;
  }

  /** Revive a context iOS suspended while backgrounded. Safe to call anytime. */
  resume() {
    if (this.ctx && this.ctx.state === "suspended") this.ctx.resume?.().catch(() => {});
  }

  toggleMute() {
    this.muted = !this.muted;
    localStorage.setItem("va-muted", this.muted ? "1" : "0");
    if (this.master) this.master.gain.value = this.muted ? 0 : 0.5;
    return this.muted;
  }

  // Own-ship engine drone. `jet` swaps the piston saws for the turbojet
  // roar+whine. Both chains read the throttle AFTER damage scaling, so a
  // flamed-out engine still tells its story: the piston drone falls to an
  // idle mutter, the turbine whine spools down out from under you.
  setFlight(throttle, speed, alive, jet = false) {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const target = alive && !jet ? 0.1 + throttle * 0.12 : 0;
    this.engineGain.gain.setTargetAtTime(target, t, 0.1);
    if (!jet) {
      const f = 50 + throttle * 60 + speed * 0.12;
      this.osc1.frequency.setTargetAtTime(f, t, 0.1);
      this.osc2.frequency.setTargetAtTime(f * 1.027, t, 0.1);
    }
    // Jet chain: the roar's brightness and the whine's pitch ARE the spool.
    // The slow time constants are deliberate — a turbine answers the lever
    // with a lag a piston engine doesn't have.
    // Mix level: the roar is broadband noise, and noise at the same gain
    // reads several dB louder than the piston chain's two saws — the first
    // phone playtest came back "the thrust sound is too loud". These sit
    // ~4 dB under the first cut, targeting the piston drone's perceived
    // level, not its number.
    this.jetGain.gain.setTargetAtTime(alive && jet ? 0.055 + throttle * 0.095 : 0, t, 0.12);
    this.whineGain.gain.setTargetAtTime(alive && jet ? 0.01 + throttle * 0.021 : 0, t, 0.12);
    if (jet) {
      this.jetFilter.frequency.setTargetAtTime(340 + throttle * 1900 + speed * 1.2, t, 0.3);
      const wf = 950 + throttle * 1750 + speed * 1.4;
      this.whineOsc.frequency.setTargetAtTime(wf, t, 0.35);
      this.whineFilter.frequency.setTargetAtTime(wf, t, 0.35);
    }
    this.windGain.gain.setTargetAtTime(alive ? Math.min(speed / 600, 0.3) : 0, t, 0.2);
  }

  // Positional engine voice for one nearby plane, silent until updateFlybys
  // feeds it. Props get the two detuned saws through a lowpass; jets get a
  // noise roar (Doppler via playbackRate) with a whistling turbine whine —
  // so a Starfang tearing past the camera sounds like a jet, not a Sparrowhawk.
  _makeFlyby(id, jet) {
    const ctx = this.ctx;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    const pan = ctx.createStereoPanner();
    pan.connect(this.master);
    let v;
    if (jet) {
      gain.connect(pan);
      this.flybyJetBuf ??= this.noiseBuffer(2.3);
      const src = ctx.createBufferSource();
      src.buffer = this.flybyJetBuf;
      src.loop = true;
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 700;
      const whine = ctx.createOscillator();
      whine.type = "sawtooth";
      whine.frequency.value = 1100;
      const wbp = ctx.createBiquadFilter();
      wbp.type = "bandpass";
      wbp.frequency.value = 1100;
      wbp.Q.value = 6;
      const wg = ctx.createGain();
      wg.gain.value = 0.25; // the whine rides under the roar
      src.connect(lp);
      lp.connect(gain);
      whine.connect(wbp);
      wbp.connect(wg);
      wg.connect(gain);
      src.start();
      whine.start();
      v = { jet: true, src, lp, whine, wbp, gain, pan, idle: 0 };
    } else {
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      osc1.type = "sawtooth";
      osc2.type = "sawtooth";
      osc1.frequency.value = 60;
      osc2.frequency.value = 61.6;
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 380;
      osc1.connect(gain);
      osc2.connect(gain);
      gain.connect(lp);
      lp.connect(pan);
      osc1.start();
      osc2.start();
      v = { jet: false, osc1, osc2, gain, pan, idle: 0 };
    }
    this.flybys.set(id, v);
    return v;
  }

  // Stop and unhook one flyby voice, whichever engine it carries.
  _retireFlyby(id) {
    const v = this.flybys.get(id);
    if (!v) return;
    for (const n of v.jet ? [v.src, v.whine] : [v.osc1, v.osc2]) n.stop();
    v.gain.disconnect();
    v.pan.disconnect();
    this.flybys.delete(id);
  }

  // Spatialized engine sound for nearby planes, so one tearing past the camera
  // roars and pans while distant traffic stays silent. `planes` is an array of
  // {id, x, y, z, throttle, speed, jet}; `camPos` and `camRight` are the listener's
  // world position and right-hand unit vector. Voices fade and retire as their
  // plane leaves range. Call once per frame while spectating.
  updateFlybys(planes, camPos, camRight) {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const MAX = 500; // metres; beyond this a plane is inaudible
    const seen = new Set();
    for (const p of planes) {
      const dx = p.x - camPos.x, dy = p.y - camPos.y, dz = p.z - camPos.z;
      const d = Math.hypot(dx, dy, dz);
      if (d > MAX) continue;
      seen.add(p.id);
      // A respawn can swap the hull under a live voice (Sparrowhawk out,
      // Starfang back) — retire the stale engine and build the right one.
      const stale = this.flybys.get(p.id);
      if (stale && stale.jet !== !!p.jet) this._retireFlyby(p.id);
      const v = this.flybys.get(p.id) ?? this._makeFlyby(p.id, !!p.jet);
      v.idle = 0;
      const inv = 1 / (d || 1);
      // Quadratic falloff: a close pass swells hard, mid-range is a murmur.
      const near = 1 - d / MAX;
      // Jet flybys ride slightly UNDER the prop number: the roar is noise,
      // and noise reads louder than saws at the same gain (see setFlight).
      v.gain.gain.setTargetAtTime(near * near * (v.jet ? 0.11 : 0.13), t, 0.08);
      // Pan from the plane's bearing across the camera's right axis.
      v.pan.pan.setTargetAtTime(Math.max(-1, Math.min(1, (dx * camRight.x + dy * camRight.y + dz * camRight.z) * inv)), t, 0.08);
      // Faked Doppler: radial velocity along the camera->plane line (+receding,
      // -approaching) shifts the pitch up on approach and down past, clamped to
      // a musical range. (WebAudio has no native Doppler since PannerNode lost
      // it.) 343 = speed of sound m/s.
      const vr = (p.vx * dx + p.vy * dy + p.vz * dz) * inv;
      const doppler = Math.max(0.72, Math.min(1.6, 343 / (343 + vr)));
      if (v.jet) {
        // The roar Dopplers by resampling the noise loop; the whine by pitch.
        v.src.playbackRate.setTargetAtTime(doppler, t, 0.08);
        v.lp.frequency.setTargetAtTime((500 + p.throttle * 1500 + p.speed * 1.0) * doppler, t, 0.08);
        const wf = (900 + p.throttle * 1600 + p.speed * 1.2) * doppler;
        v.whine.frequency.setTargetAtTime(wf, t, 0.08);
        v.wbp.frequency.setTargetAtTime(wf, t, 0.08);
      } else {
        const f = (52 + p.throttle * 55 + p.speed * 0.1) * doppler;
        v.osc1.frequency.setTargetAtTime(f, t, 0.08);
        v.osc2.frequency.setTargetAtTime(f * 1.026, t, 0.08);
      }
    }
    // Fade out planes that left range; retire the voice once it's been gone a
    // while (≈1.5 s at 60 fps) so we don't leak oscillators.
    for (const [id, v] of this.flybys) {
      if (seen.has(id)) continue;
      v.gain.gain.setTargetAtTime(0, t, 0.25);
      if (++v.idle > 90) this._retireFlyby(id);
    }
  }

  blip({ freq = 600, type = "square", dur = 0.08, vol = 0.2, slide = 0 }) {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    if (slide) osc.frequency.exponentialRampToValueAtTime(Math.max(40, freq + slide), t + dur);
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    osc.connect(g);
    g.connect(this.master);
    osc.start(t);
    osc.stop(t + dur);
  }

  noiseBurst({ dur = 0.1, vol = 0.25, freq = 1200, q = 1 }) {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const src = this.ctx.createBufferSource();
    src.buffer = this.noiseBuffer(dur);
    const filter = this.ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.value = freq;
    filter.Q.value = q;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    src.connect(filter);
    filter.connect(g);
    g.connect(this.master);
    src.start(t);
  }

  gunshot() {
    this.noiseBurst({ dur: 0.07, vol: 0.18, freq: 900, q: 0.7 });
  }

  // Supersonic crack of a round passing within feet: a hard high snap panned
  // to the side it passed, with a softer low whip trailing 40 ms behind.
  crack(pan = 0) {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const panner = this.ctx.createStereoPanner();
    panner.pan.value = Math.max(-1, Math.min(1, pan));
    panner.connect(this.master);
    const burst = (at, { dur, vol, freq, q }) => {
      const src = this.ctx.createBufferSource();
      src.buffer = this.noiseBuffer(dur);
      const filter = this.ctx.createBiquadFilter();
      filter.type = "bandpass";
      filter.frequency.value = freq;
      filter.Q.value = q;
      const g = this.ctx.createGain();
      g.gain.setValueAtTime(vol, at);
      g.gain.exponentialRampToValueAtTime(0.001, at + dur);
      src.connect(filter);
      filter.connect(g);
      g.connect(panner);
      src.start(at);
    };
    burst(t, { dur: 0.03, vol: 0.32, freq: 3500, q: 2 });
    burst(t + 0.04, { dur: 0.09, vol: 0.1, freq: 700, q: 0.8 });
  }

  // Kill-confirm stamp: a low thump with a short rising tail under the
  // banner — weightier than the hitmarker, far shorter than an explosion.
  killConfirm() {
    this.noiseBurst({ dur: 0.22, vol: 0.26, freq: 200, q: 0.8 });
    this.blip({ freq: 320, type: "square", dur: 0.28, vol: 0.13, slide: 260 });
  }

  // CHECK SIX sting: two short urgent blips sliding down — unmistakably a
  // warning, distinct from the hitmarker's single bright tick.
  checkSix() {
    this.blip({ freq: 1150, type: "square", dur: 0.09, vol: 0.22, slide: -350 });
    setTimeout(() => this.blip({ freq: 950, type: "square", dur: 0.11, vol: 0.22, slide: -400 }), 110);
  }

  // Ambient civilian audio (roadmap #42). A seagull mew: two short descending
  // squawks, panned to where the flock is. pan in [-1, 1].
  gull(pan = 0) {
    if (!this.ctx) return;
    const panner = this.ctx.createStereoPanner();
    panner.pan.value = Math.max(-1, Math.min(1, pan));
    panner.connect(this.master);
    const cry = (at, freq) => {
      const osc = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(freq, at);
      osc.frequency.exponentialRampToValueAtTime(freq * 0.6, at + 0.12);
      g.gain.setValueAtTime(0.0, at);
      g.gain.linearRampToValueAtTime(0.06, at + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, at + 0.16);
      osc.connect(g);
      g.connect(panner);
      osc.start(at);
      osc.stop(at + 0.18);
    };
    const t = this.ctx.currentTime;
    cry(t, 1400);
    cry(t + 0.22, 1250);
  }

  // A tank main-gun report: a sharp, heavy crack with a low thump under it —
  // punchier and deeper than the light gunshot, shorter than a blast.
  cannon() {
    this.noiseBurst({ dur: 0.18, vol: 0.3, freq: 320, q: 0.6 });
    this.blip({ freq: 120, type: "sine", dur: 0.22, vol: 0.22, slide: -70 });
  }

  // A warship main-battery salvo: a muzzle crack riding a long rolling boom
  // with a sub-bass swell under it — far longer and deeper than the tank
  // cannon's bark. k is the battery's weight (1 = a battleship broadside,
  // ~0.45 a destroyer's 5in): heavier guns boom lower and hang longer.
  // dist (m) fades another ship's salvo across the water.
  salvo(k = 1, dist = 0) {
    if (!this.ctx) return;
    const vol = Math.max(0.05, 0.6 - dist / 3500);
    const dur = 1.1 + 1.1 * k;
    this.noiseBurst({ dur: 0.1, vol: vol * 0.55, freq: 500, q: 0.6 }); // muzzle crack
    this.noiseBurst({ dur, vol: vol * 0.85, freq: 70 + 50 * (1 - k), q: 0.3 }); // rolling boom
    this.blip({ freq: 46 + 22 * (1 - k), type: "sine", dur: dur * 0.9, vol, slide: -14 }); // sub swell
  }

  // Jets-era seeker head (docs/early-jets-era.md): the classic two-voice
  // story — a warbling growl while the head is tracking, a steady high tone
  // at full lock, silence when caged. One persistent oscillator, lazily
  // built, driven by mode ("track" | "lock" | anything else = off) once per
  // frame from main.js.
  setSeeker(mode) {
    if (!this.ctx) return;
    if (!this.seekerOsc) {
      const osc = this.ctx.createOscillator();
      osc.type = "square";
      osc.frequency.value = 540;
      const g = this.ctx.createGain();
      g.gain.value = 0;
      osc.connect(g);
      g.connect(this.master);
      osc.start();
      // The growl is the tone warbling: an LFO into the frequency.
      const lfo = this.ctx.createOscillator();
      lfo.type = "sine";
      lfo.frequency.value = 13;
      const lfoGain = this.ctx.createGain();
      lfoGain.gain.value = 0;
      lfo.connect(lfoGain);
      lfoGain.connect(osc.frequency);
      lfo.start();
      this.seekerOsc = osc;
      this.seekerGain = g;
      this.seekerLfoGain = lfoGain;
    }
    const t = this.ctx.currentTime;
    if (mode === "track") {
      this.seekerGain.gain.setTargetAtTime(0.045, t, 0.06);
      this.seekerOsc.frequency.setTargetAtTime(540, t, 0.06);
      this.seekerLfoGain.gain.setTargetAtTime(90, t, 0.06);
    } else if (mode === "lock") {
      this.seekerGain.gain.setTargetAtTime(0.06, t, 0.04);
      this.seekerOsc.frequency.setTargetAtTime(940, t, 0.04);
      this.seekerLfoGain.gain.setTargetAtTime(0, t, 0.04);
    } else {
      this.seekerGain.gain.setTargetAtTime(0, t, 0.08);
    }
  }

  // A heat-seeker off the rail: a hard whoosh that fades with distance —
  // your own launch roars, a far one is a whisper of trouble.
  missileLaunch(dist = 0) {
    const vol = Math.max(0.06, 0.3 - dist / 3000);
    this.noiseBurst({ dur: 0.5, vol, freq: 500, q: 0.5 });
    this.blip({ freq: 300, type: "sawtooth", dur: 0.45, vol: vol * 0.6, slide: 260 });
  }

  // A countermeasure flare punching out: a bright pop and a burning hiss.
  flarePop() {
    this.blip({ freq: 900, type: "square", dur: 0.06, vol: 0.12, slide: -300 });
    this.noiseBurst({ dur: 0.3, vol: 0.08, freq: 2400, q: 0.8 });
  }

  // Jets-era RWR: the sound of a guidance radar deciding about you. Track is
  // a slow three-chirp sweep; launch (urgent=true) is a fast climbing
  // rattle — unmistakably worse. Distinct from checkSix's two-blip sting.
  rwrChirp(urgent = false) {
    const n = urgent ? 6 : 3;
    const gap = urgent ? 70 : 220;
    for (let i = 0; i < n; i++) {
      setTimeout(
        () =>
          this.blip({
            freq: urgent ? 1250 + i * 60 : 780,
            type: "square",
            dur: urgent ? 0.05 : 0.09,
            vol: 0.18,
          }),
        i * gap,
      );
    }
  }

  hitmarker() {
    this.blip({ freq: 1500, type: "square", dur: 0.05, vol: 0.12 });
  }

  damaged() {
    this.blip({ freq: 220, type: "sawtooth", dur: 0.15, vol: 0.2, slide: -120 });
  }

  // Pre-stall airframe buffet: a low filtered rumble, called on a throttle
  // while the wing is near the stall (intensity 0..1 deepens it).
  buffet(intensity = 1) {
    this.noiseBurst({ dur: 0.13, vol: 0.05 + 0.13 * intensity, freq: 90, q: 0.5 });
  }

  explosion(dist = 0) {
    const vol = Math.max(0.05, 0.5 - dist / 2500);
    this.noiseBurst({ dur: 0.9, vol, freq: 160, q: 0.4 });
    this.blip({ freq: 90, type: "sine", dur: 0.6, vol: vol * 0.9, slide: -50 });
  }

  // Thunder: the sky's own artillery. Called at strike time with the bolt's
  // range; the sound arrives late like the real thing (~340 m/s), a hard
  // crack up close, a long rolling grumble from across the map.
  thunder(dist = 0) {
    if (!this.ctx) return;
    const delayMs = Math.min(dist / 340, 8) * 1000;
    setTimeout(() => {
      const vol = Math.max(0.05, 0.5 - dist / 6000);
      if (dist < 700) this.noiseBurst({ dur: 0.22, vol: vol * 0.8, freq: 1500, q: 0.8 });
      this.noiseBurst({ dur: 2.6, vol, freq: 75, q: 0.35 });
      this.blip({ freq: 48, type: "sine", dur: 1.8, vol: vol * 0.7, slide: -14 });
    }, delayMs);
  }

  fanfare(win) {
    const seq = win ? [520, 660, 780, 1040] : [400, 340, 290, 240];
    seq.forEach((f, i) =>
      setTimeout(() => this.blip({ freq: f, type: "square", dur: 0.16, vol: 0.16 }), i * 140),
    );
  }
}
